import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Camera, X, Upload, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
    { id: 'bike', name: 'Bikes' },
    { id: 'gear', name: 'Gear' },
    { id: 'parts', name: 'Parts' },
    { id: 'accessory', name: 'Accessories' },
    { id: 'other', name: 'Other' },
];

const CONDITIONS = [
    { id: 'new', name: 'New' },
    { id: 'like_new', name: 'Like New' },
    { id: 'good', name: 'Good' },
    { id: 'fair', name: 'Fair' },
    { id: 'poor', name: 'Poor' },
];

export default function CreateListing() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [images, setImages] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'gear',
        condition: 'good',
        price_boltz: '',
        price_cad: '',
        location: '',
    });

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (images.length + files.length > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }

        setUploading(true);

        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} is not an image`);
                continue;
            }

            if (file.size > 10 * 1024 * 1024) {
                toast.error(`${file.name} is too large (max 10MB)`);
                continue;
            }

            try {
                const formData = new FormData();
                formData.append('file', file);

                const res = await fetch(`${API}/upload/image`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                });

                if (res.ok) {
                    const data = await res.json();
                    setImages(prev => [...prev, {
                        file_id: data.file_id,
                        url: `${API}/files/${data.file_id}`,
                        preview: URL.createObjectURL(file)
                    }]);
                } else {
                    const error = await res.json();
                    toast.error(error.detail || 'Failed to upload image');
                }
            } catch (error) {
                toast.error('Failed to upload image');
            }
        }

        setUploading(false);
    };

    const removeImage = (index) => {
        setImages(prev => {
            const newImages = [...prev];
            if (newImages[index].preview) {
                URL.revokeObjectURL(newImages[index].preview);
            }
            newImages.splice(index, 1);
            return newImages;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Please enter a title');
            return;
        }

        if (!formData.price_boltz || parseFloat(formData.price_boltz) <= 0) {
            toast.error('Please enter a valid Boltz price');
            return;
        }

        setSubmitting(true);

        try {
            const listingData = {
                title: formData.title,
                description: formData.description,
                category: formData.category,
                condition: formData.condition,
                price_boltz: parseFloat(formData.price_boltz),
                price_cad: formData.price_cad ? parseFloat(formData.price_cad) : null,
                location: formData.location || null,
                image_url: images[0]?.url || null,
                images: images.map(img => img.url),
            };

            const res = await fetch(`${API}/listings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(listingData),
            });

            if (res.ok) {
                toast.success('Listing created!');
                navigate('/shop');
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to create listing');
            }
        } catch (error) {
            toast.error('Failed to create listing');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigate('/shop')}
                        className="mb-4"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Back to Shop
                    </Button>
                    <span className="label-text text-primary">P2P MARKETPLACE</span>
                    <h1 className="text-2xl font-bold mt-1">Create Listing</h1>
                </motion.div>

                <form onSubmit={handleSubmit}>
                    {/* Image Upload */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <Card className="glass-card mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Camera size={20} />
                                    Photos
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                                    {images.map((img, index) => (
                                        <div 
                                            key={img.file_id} 
                                            className="relative aspect-square rounded-lg overflow-hidden border border-border"
                                        >
                                            <img 
                                                src={img.preview || img.url} 
                                                alt={`Upload ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute top-1 right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                                            >
                                                <X size={14} />
                                            </button>
                                            {index === 0 && (
                                                <span className="absolute bottom-1 left-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                                                    Main
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {images.length < 5 && (
                                        <label className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary cursor-pointer flex flex-col items-center justify-center transition-colors">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={uploading}
                                            />
                                            {uploading ? (
                                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <Upload size={24} className="text-muted-foreground" />
                                                    <span className="text-xs text-muted-foreground mt-1">Add</span>
                                                </>
                                            )}
                                        </label>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Add up to 5 photos. First photo will be the main image.
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Details */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <Card className="glass-card mb-6">
                            <CardHeader>
                                <CardTitle>Details</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="title">Title *</Label>
                                    <Input
                                        id="title"
                                        value={formData.title}
                                        onChange={e => setFormData({...formData, title: e.target.value})}
                                        placeholder="What are you selling?"
                                        maxLength={100}
                                        data-testid="listing-title"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={e => setFormData({...formData, description: e.target.value})}
                                        placeholder="Describe your item..."
                                        rows={4}
                                        data-testid="listing-description"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Category</Label>
                                        <Select 
                                            value={formData.category} 
                                            onValueChange={v => setFormData({...formData, category: v})}
                                        >
                                            <SelectTrigger data-testid="listing-category">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CATEGORIES.map(cat => (
                                                    <SelectItem key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <Label>Condition</Label>
                                        <Select 
                                            value={formData.condition} 
                                            onValueChange={v => setFormData({...formData, condition: v})}
                                        >
                                            <SelectTrigger data-testid="listing-condition">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CONDITIONS.map(cond => (
                                                    <SelectItem key={cond.id} value={cond.id}>
                                                        {cond.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="location">Location</Label>
                                    <Input
                                        id="location"
                                        value={formData.location}
                                        onChange={e => setFormData({...formData, location: e.target.value})}
                                        placeholder="City, Province"
                                        data-testid="listing-location"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Pricing */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card className="glass-card mb-6">
                            <CardHeader>
                                <CardTitle>Pricing</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="price_boltz">Price (Boltz) *</Label>
                                    <Input
                                        id="price_boltz"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.price_boltz}
                                        onChange={e => setFormData({...formData, price_boltz: e.target.value})}
                                        placeholder="100"
                                        data-testid="listing-price-rmr"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        10% platform fee applies to marketplace sales
                                    </p>
                                </div>

                                <div>
                                    <Label htmlFor="price_cad">Price (CAD) - Optional</Label>
                                    <Input
                                        id="price_cad"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.price_cad}
                                        onChange={e => setFormData({...formData, price_cad: e.target.value})}
                                        placeholder="99.99"
                                        data-testid="listing-price-cad"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Submit */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full btn-primary text-lg py-6"
                            data-testid="submit-listing"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                                    Creating...
                                </>
                            ) : (
                                'Create Listing'
                            )}
                        </Button>
                    </motion.div>
                </form>
            </div>
        </Layout>
    );
}
